import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  Renderer2,
} from '@angular/core';
import { translate } from '@jsverse/transloco';

import { FileItem, FileUploader, FileUploadModule } from 'ng2-file-upload';

import { AppSharedModule } from 'app/app-shared.module';
import { AlertService } from 'app/services/alert.service';
import { LoggerAdditional, LoggerService } from 'app/services/logger.service';
import { AuthService } from '../../providers/bridge-api/auth.service';

@Component({
  selector: 'app-file-upload',
  imports: [AppSharedModule, FileUploadModule],
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss'],
})
export class FileUploadComponent<T> implements OnInit {
  @Input() dragAndDropText: string;
  @Input() buttonText: string;
  @Input() set size(size) {
    this._size = size ?? 'normal';
    switch (this._size) {
      case 'normal':
        this.buttonSize = 'xxlarge';
        break;
      case 'small':
        this.buttonSize = 'normal';
        break;
      case 'x-small':
        this.buttonSize = 'small';
        break;
      case 'xx-small':
        this.buttonSize = 'xsmall';
        break;
    }
    this.renderer.addClass(this.elementRef.nativeElement, `size--${size}`);
  }
  @Input() allowedExtensions? = ['json'];
  @Input() uploadNotes?: string[] = [];
  @Input() itemValidator: (fileUpload, importedItem) => { isValid: boolean; message: string };
  @Input() mode: 'local' | 'server' = 'local';
  @Input() progressIndicator: 'spinner' | 'bar' = 'spinner';
  @Input() notificationType: 'paragraph' | 'snackbar' = 'paragraph';
  @Input() requiresConfirmation?: boolean = false;

  @Output() fileAdded = new EventEmitter<{ fileItem: FileItem; importedItem?: T }>();
  @Output() fileUploaded = new EventEmitter<T>();
  @Output() fileConfirmation = new EventEmitter<{ files: FileItem[] }>();
  @Output() fileError = new EventEmitter<any>();

  filesToConfirm: FileItem[] = [];

  readonly validExtensions = ['json', 'csv', 'xlsx', 'xls'];
  readonly MIMETypes = {
    json: 'application/json',
    csv: 'text/csv',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    xls: 'application/vnd.ms-excel',
  };

  readonly sizeMultipliers = {
    normal: 1,
    small: 0.7,
    'x-small': 0.5,
    'xx-small': 0.35,
  };
  readonly maxFiles = 1;

  allowedMIMETypes = [this.MIMETypes.json];

  private _size: 'normal' | 'small' | 'x-small' | 'xx-small' = 'normal';
  buttonSize = 'xxlarge';

  itemReaderFactory = (extension) => {
    if (!this.validExtensions.includes(extension)) {
      throw new Error(`Extension ${extension} not supported`);
    }

    switch (extension) {
      case 'json':
        return (content) => JSON.parse(content);
      case 'csv':
        return (content) => content;
      case 'xlsx':
      case 'xls':
        return (content) => content;
    }
  };
  itemReader: (content) => T;
  importedItemDto: T;

  uploader: FileUploader;
  hasBaseDropZoneOver: boolean;
  lastErrorMessage: string;

  constructor(
    private logger: LoggerService,
    private renderer: Renderer2,
    private elementRef: ElementRef,
    private alertService: AlertService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    if (!this.dragAndDropText) {
      this.dragAndDropText = translate('file-upload.drag-and-drop');
    }
    if (!this.buttonText) {
      this.buttonText = translate('file-upload.upload-file');
    }
    this.allowedMIMETypes = this.allowedExtensions.map((ext) => this.MIMETypes[ext]);

    this.uploader = new FileUploader({
      url: 'http://will.not.be.used/',
      autoUpload: false,
      allowedMimeType: this.allowedMIMETypes,
      filters: [{ name: 'singleFile', fn: () => this.uploader.queue.length < this.maxFiles }],
    });
    this.uploader.onAfterAddingFile = this.onFileAdded.bind(this);
    if (this.mode === 'server') {
      this.uploader.authToken = this.authService.getToken();
      this.uploader.onAfterAddingFile = this.doUpload.bind(this);
      this.uploader.onErrorItem = this.onErrorItem.bind(this);
      this.uploader.onSuccessItem = this.onSuccessItem.bind(this);
    }
  }

  public onFileAdded(item: FileItem): void {
    this.prepareUpload();

    if (this.isValidFilename(item.file.name)) {
      const reader = new FileReader();
      reader.onloadend = () => {
        try {
          const importedItemDto: T = this.itemReader(reader.result);

          const validationResult = this.itemValidator(item, importedItemDto);
          if (!validationResult.isValid) {
            this.uploader.removeFromQueue(this.uploader.queue[0]);
            this.handleError(validationResult.message);
            return;
          }

          this.importedItemDto = importedItemDto;
          this.fileAdded.emit({ fileItem: item, importedItem: importedItemDto });
          this.handleFileConfirmation(item);
        } catch (err) {
          this.importedItemDto = undefined;
          this.uploader.removeFromQueue(this.uploader.queue[0]);
          const extension = item.file.name.substring(item.file.name.lastIndexOf('.') + 1);
          this.handleError(
            translate(`file-upload.error-invalid-${extension}`, {
              file: item.file.name,
            })
          );
        }
      };
      reader.readAsText(item._file);
    }
  }

  public doUpload(item: FileItem): void {
    this.prepareUpload();

    if (this.isValidFilename(item.file.name)) {
      this.fileAdded.emit({ fileItem: item });
      this.handleFileConfirmation(item);
    }
  }

  private prepareUpload() {
    this.lastErrorMessage = undefined;
  }

  private handleFileConfirmation(file: FileItem): void {
    if (this.requiresConfirmation) {
      this.filesToConfirm.push(file);
      this.fileConfirmation.emit({ files: [file] });
    }
  }

  public onErrorItem(item: FileItem, response: string, status: number): void {
    this.lastErrorMessage = response;
    const additional: LoggerAdditional = {
      file: {
        name: item.file.name,
        size: item.file.size,
      },
      status,
    };
    if (!response) {
      this.lastErrorMessage = `HTTP code ${status}`;
    } else if (response.startsWith('{')) {
      const payload = JSON.parse(response);
      if (
        payload.type === 'UploadMultipartException' ||
        payload.type === 'UploadNotValidException'
      ) {
        this.lastErrorMessage = `${translate('file-upload.not-able-to-upload')}:\n ${
          payload.description
        }`;
      } else if (payload.description) {
        this.lastErrorMessage = `${payload.type}: ${payload.description}`;
      }

      if (payload.data) {
        this.lastErrorMessage += ` (${payload.data})`;
      }

      additional.error = payload;
    } else {
      additional.response = response;
    }
    this.handleError(this.lastErrorMessage);
    item.remove();
    this.fileError.emit(additional);
    this.logger.error('UploadFailed', additional);
  }

  public onSuccessItem(item: FileItem, response: string): void {
    try {
      this.fileUploaded.emit(JSON.parse(response));
    } catch {
      this.handleError(translate('file-upload.invalid-response-from-server'));
      item.isError = true;
      item.isSuccess = false;
    }
    item.remove();
  }

  handleError(error: string) {
    this.lastErrorMessage = error;

    if (this.notificationType === 'snackbar') {
      this.alertService.showErrorToast(error);
    }
  }

  // when a file is added, check that it has a valid extension
  public validateSelectedFiles(files: FileList): void {
    const file: File = files[0];
    if (!file) {
      return;
    }

    // only [maxFiles] file allowed
    if (files.length > this.maxFiles) {
      this.alertService.showToast(
        translate('file-upload.error-multiple-files', { count: this.maxFiles })
      );
      this.uploader.queue.splice(1);
    }

    if (!this.isValidFilename(file.name)) {
      // not valid, don't allow it to be added
      this.handleError(translate('file-upload.error-invalid-extension', { file: file.name }));
      this.uploader.queue.splice(0, 1);
      this.importedItemDto = undefined;
    }
  }

  // valididate filename by extension against allowed list
  public isValidFilename(filename: string): boolean {
    const ext: string = filename.substring(filename.lastIndexOf('.') + 1);
    if (this.mode === 'local') {
      try {
        this.itemReader = this.itemReaderFactory(ext);
      } catch (error) {
        this.logger.error('isValidFileName error', { error });
      }
    }
    return this.allowedExtensions.includes(ext.toLowerCase());
  }

  fileOverBase(e: boolean): void {
    this.hasBaseDropZoneOver = e;
  }

  get size() {
    return this._size;
  }
}
